import type { MaturityLevel, DiagnosticPillarKey } from '../types';

/**
 * Returns a Fluent UI badge color based on score value.
 */
export function getScoreColor(score: number): 'success' | 'warning' | 'danger' {
  if (score >= 70) return 'success';
  if (score >= 40) return 'warning';
  return 'danger';
}

/**
 * Returns a hex color for the maturity score gauge.
 * Red < 40, Orange 40-70, Green > 70
 */
export function getScoreHex(score: number): string {
  if (score > 70) return '#0E7A0D';
  if (score >= 40) return '#C4A000';
  return '#D13438';
}

/**
 * Derives the maturity level label from a 0-100 score.
 */
export function getMaturityLevel(score: number): MaturityLevel {
  if (score >= 81) return 'Optimizing';
  if (score >= 61) return 'Managed';
  if (score >= 41) return 'Defined';
  if (score >= 21) return 'Developing';
  return 'Initial';
}

/**
 * Human-readable labels for blind spot types.
 */
const BLIND_SPOT_LABELS: Record<string, string> = {
  REPO_NOT_INTEGRATED: 'Repo Not Integrated',
  PIPELINE_NOT_MONITORED: 'Pipeline Not Monitored',
  ENV_NOT_SCANNED: 'Environment Not Scanned',
  SECRETS_NOT_ROTATED: 'Secrets Not Rotated',
  IAC_DRIFT_DETECTED: 'IaC Drift Detected',
  DEPENDENCY_UNTRACKED: 'Dependency Untracked',
  MANUAL_DEPLOY_DETECTED: 'Manual Deploy Detected',
  RepoNotIntegrated: 'Repo Not Integrated',
  PipelineNotMonitored: 'Pipeline Not Monitored',
  EnvNotScanned: 'Environment Not Scanned',
  SecretsNotRotated: 'Secrets Not Rotated',
  IacDriftDetected: 'IaC Drift Detected',
  DependencyUntracked: 'Dependency Untracked',
  ManualDeployDetected: 'Manual Deploy Detected',
};

/**
 * Formats a blind spot type enum value into a human-readable label.
 * Accepts both SCREAMING_SNAKE_CASE and PascalCase from the API.
 */
export function formatBlindSpotType(type: string): string {
  return BLIND_SPOT_LABELS[type] ?? type;
}

/**
 * Human-readable labels for pillar keys.
 */
export const PILLAR_LABELS: Record<DiagnosticPillarKey, string> = {
  controlCoverage: 'Control Coverage',
  enforcementGaps: 'Enforcement Gaps',
  evidenceIntegrity: 'Evidence Confidence',
  runtimeBlindSpots: 'Runtime Blind Spots',
};

/**
 * Brand category colors for the 4 pillars.
 * Maps each pillar to a visually distinct LeftShield brand color.
 */
export const PILLAR_COLORS: Record<DiagnosticPillarKey, string> = {
  controlCoverage: '#13B5B1',    // Compliance - Cyan
  enforcementGaps: '#E852B9',    // Code - Magenta
  evidenceIntegrity: '#E89652',  // Environment - Orange
  runtimeBlindSpots: '#E85252',  // Risk - Red
};

/**
 * Returns a Fluent UI badge color for a severity string.
 */
export function getSeverityBadgeColor(severity: string): 'danger' | 'important' | 'warning' | 'informative' {
  if (severity === 'Critical') return 'danger';
  if (severity === 'High') return 'important';
  if (severity === 'Medium') return 'warning';
  return 'informative';
}

/**
 * Returns a Fluent UI badge color for a blind spot status.
 */
export function getStatusBadgeColor(status: string): 'danger' | 'warning' | 'success' | 'informative' {
  if (status === 'Open') return 'danger';
  if (status === 'Acknowledged') return 'warning';
  if (status === 'Mitigated') return 'informative';
  return 'success';
}

/**
 * Safely parse the signals JSON string returned by the API.
 * Returns an array of key-value objects for display.
 */
export function parseSignals(signalsJson: string): Array<{ key: string; value: string }> {
  try {
    const parsed = JSON.parse(signalsJson);
    if (typeof parsed === 'object' && parsed !== null) {
      return Object.entries(parsed).map(([key, value]) => ({
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value),
      }));
    }
    return [];
  } catch {
    return [];
  }
}
